/**
 * Takeoff geometry — everything works in the normalized drawing space:
 * 1000 units wide, `unitH` tall (unitH = 1000 * pageH / pageW of the
 * ROTATED page). Quantities are derived from the page's feet-per-unit scale.
 */
import { polyLen, polyArea } from "./pricing";

export const unitHeight = (pageW, pageH) => (pageW > 0 ? (1000 * pageH) / pageW : 1000);

/** 12.33 -> `12' 4"` */
export const fmtFtIn = (ft) => {
  const n = Number(ft) || 0;
  const neg = n < 0;
  let inches = Math.round(Math.abs(n) * 12);
  const f = Math.floor(inches / 12);
  const i = inches % 12;
  return `${neg ? "-" : ""}${f}' ${i}"`;
};

/**
 * Feet per drawing unit for a standard preset. ratio = real inches per
 * paper inch (1/4"=1' -> 48; metric 1:100 -> 100). paperWidthInches comes
 * from the PDF (points/72) or an image at 96 px/inch.
 */
export const feetPerUnitFromPreset = (ratio, paperWidthInches) =>
  (paperWidthInches * ratio) / 12 / 1000;

/** Quantity of a measured item at a given feet-per-unit page scale. */
export const measureQuantity = (type, points, fpu) => {
  if (!points || !points.length) return 0;
  if (type === "linear") return polyLen(points) * (fpu || 0);
  if (type === "area") return polyArea(points) * (fpu || 0) * (fpu || 0);
  return 1; // count
};

// ---------------- hit testing ----------------

export const distToSegment = (p, a, b) => {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  let t = len2 ? ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const x = a.x + t * dx, y = a.y + t * dy;
  return { dist: Math.hypot(p.x - x, p.y - y), t, point: { x, y } };
};

/** Nearest point anywhere on a polyline. */
export const nearestOnPolyline = (pts, p) => {
  let best = { dist: Infinity, seg: 0, t: 0, point: pts[0] || { x: 0, y: 0 } };
  for (let i = 1; i < pts.length; i++) {
    const r = distToSegment(p, pts[i - 1], pts[i]);
    if (r.dist < best.dist) best = { dist: r.dist, seg: i - 1, t: r.t, point: r.point };
  }
  return best;
};

export const pointInPolygon = (pts, p) => {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i], b = pts[j];
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
};

/** Does a pointer at p (units) hit this item? tol is in drawing units. */
export const hitTestItem = (item, p, tol) => {
  const pts = item.points || [];
  if (!pts.length) return false;
  if (item.type === "count") return Math.hypot(p.x - pts[0].x, p.y - pts[0].y) <= tol;
  if (item.type === "linear") return nearestOnPolyline(pts, p).dist <= tol;
  // area: inside, or near an edge
  if (pointInPolygon(pts, p)) return true;
  for (let i = 0; i < pts.length; i++) {
    if (distToSegment(p, pts[i], pts[(i + 1) % pts.length]).dist <= tol) return true;
  }
  return false;
};

/** Box-select intersection test (rect in units: {x,y,w,h}). */
export const rectIntersectsItem = (item, r) => {
  const inRect = (p) => p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
  const pts = item.points || [];
  if (pts.some(inRect)) return true;
  if (item.type === "count") return false;
  // any segment crossing the rect?
  const corners = [
    { x: r.x, y: r.y }, { x: r.x + r.w, y: r.y },
    { x: r.x + r.w, y: r.y + r.h }, { x: r.x, y: r.y + r.h },
  ];
  const segs = item.type === "area"
    ? pts.map((a, i) => [a, pts[(i + 1) % pts.length]])
    : pts.slice(1).map((b, i) => [pts[i], b]);
  const ccw = (a, b, c) => (c.y - a.y) * (b.x - a.x) > (b.y - a.y) * (c.x - a.x);
  const crosses = (a, b, c, d) => ccw(a, c, d) !== ccw(b, c, d) && ccw(a, b, c) !== ccw(a, b, d);
  for (const [a, b] of segs)
    for (let i = 0; i < 4; i++)
      if (crosses(a, b, corners[i], corners[(i + 1) % 4])) return true;
  return false;
};

// ---------------- cut ----------------

/**
 * Split a linear polyline at the point nearest p. Returns [ptsA, ptsB] or
 * null when the nearest point is too close to an endpoint to be useful.
 * Total length is preserved (the cut point is shared by both halves).
 */
export const cutPolyline = (pts, p, minEndUnits = 3) => {
  if (!pts || pts.length < 2) return null;
  const near = nearestOnPolyline(pts, p);
  const a = [...pts.slice(0, near.seg + 1), near.point];
  const b = [near.point, ...pts.slice(near.seg + 1)];
  if (polyLen(a) < minEndUnits || polyLen(b) < minEndUnits) return null;
  return [a, b];
};

// ---------------- draft helpers ----------------

/** Snap the ray from->to to the nearest 45-degree increment (S toggle). */
export const snap45 = (from, to) => {
  const ang = Math.atan2(to.y - from.y, to.x - from.x);
  const snapped = Math.round(ang / (Math.PI / 4)) * (Math.PI / 4);
  const len = Math.hypot(to.x - from.x, to.y - from.y);
  return { x: from.x + Math.cos(snapped) * len, y: from.y + Math.sin(snapped) * len };
};

// ---------------- selection groups / copy-paste ----------------

export const groupBounds = (items) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const it of items)
    for (const p of it.points || []) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  if (minX === Infinity) return null;
  return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
};

/** Return copies of items translated by (dx, dy) drawing units. */
export const translateItems = (items, dx, dy) =>
  items.map((it) => ({ ...it, points: (it.points || []).map((p) => ({ x: p.x + dx, y: p.y + dy })) }));

/** Shift a group so its bounds sit fully inside the page. */
export const clampGroup = (items, unitH) => {
  const b = groupBounds(items);
  if (!b) return items;
  let dx = 0, dy = 0;
  if (b.minX < 0) dx = -b.minX;
  else if (b.maxX > 1000) dx = 1000 - b.maxX;
  if (b.minY < 0) dy = -b.minY;
  else if (b.maxY > unitH) dy = unitH - b.maxY;
  return dx || dy ? translateItems(items, dx, dy) : items;
};

/** Rescale vertical coordinates when pasting onto a differently-shaped page. */
export const rescaleY = (items, fromUnitH, toUnitH) => {
  if (!fromUnitH || !toUnitH || Math.abs(fromUnitH - toUnitH) < 0.001) return items;
  const k = toUnitH / fromUnitH;
  return items.map((it) => ({ ...it, points: (it.points || []).map((p) => ({ x: p.x, y: p.y * k })) }));
};

export const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
